import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle, XCircle, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ClientRegister = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [invitationData, setInvitationData] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [siteName, setSiteName] = useState('SlabReality');

  useEffect(() => {
    // Fetch site name
    axios.get(`${API}/admin/settings/public/site-name`)
      .then(res => { if (res.data?.site_name) setSiteName(res.data.site_name); })
      .catch(() => {});
    
    if (!token) {
      setError('Invalid invitation link');
      setLoading(false);
      return;
    }
    
    // Verify token
    const verifyToken = async () => {
      try {
        const response = await axios.get(`${API}/invitations/verify/${token}`);
        setInvitationData(response.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Invalid or expired invitation');
      } finally {
        setLoading(false);
      }
    };
    
    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/invitations/accept/${token}`, { password });
      
      // Store token
      localStorage.setItem('session_token', response.data.token);
      
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create account');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 px-4">
        <div className="max-w-md w-full text-center">
          <XCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
          <h1 className="text-2xl font-medium mb-2">Invalid Invitation</h1>
          <p className="text-stone-600 mb-6">{error}</p>
          <Link to="/">
            <Button variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-stone-50">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-stone-900 items-center justify-center p-12">
        <div className="text-white max-w-md">
          <CheckCircle className="w-16 h-16 mb-6 text-green-400" />
          <h2 className="text-3xl font-light mb-4">You've Been Invited</h2>
          <p className="text-stone-300 text-lg">
            <strong className="text-white">{invitationData?.inviter_name}</strong>
            {invitationData?.inviter_company && (
              <> from <strong className="text-white">{invitationData.inviter_company}</strong></>
            )}
            {' '}has invited you to join {siteName}.
          </p>
        </div>
      </div>
      
      {/* Right Panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-sm text-stone-600 hover:text-stone-900 mb-6"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </button>
          
          <Link to="/" className="heading-display text-2xl font-medium mb-8 block">
            {siteName}
          </Link>
          
          <h1 className="text-3xl font-medium mb-2">Complete Your Registration</h1>
          <p className="text-stone-600 mb-8">
            Create a password to access your account.
          </p>
          
          <div className="bg-stone-100 p-4 mb-8">
            <p className="text-sm text-stone-600">Your account details:</p>
            <p className="font-medium">{invitationData?.first_name} {invitationData?.last_name}</p>
            <p className="text-stone-600">{invitationData?.email}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="password" className="mb-2 block">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Create a password"
                required
                minLength={6}
              />
            </div>
            
            <div>
              <Label htmlFor="confirmPassword" className="mb-2 block">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                required
              />
            </div>
            
            <Button 
              type="submit" 
              className="w-full h-12 bg-stone-900 hover:bg-stone-800"
              disabled={submitting}
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating Account...</>
              ) : (
                'Accept Invitation & Create Account'
              )}
            </Button>
          </form>
          
          <p className="text-center text-sm text-stone-500 mt-8">
            By accepting, you agree to our Terms of Service and Privacy Policy.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ClientRegister;
